import { useEffect, useMemo, useState } from 'react';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import useDragList from '../../components/drag/useDragList';
import { toast } from 'sonner';
import { PageHeader, Loading } from '../../components/office';
import { useSite } from '../../contexts/SiteContext';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { financialConfigApi, kitchenScreensApi, menuApi, menuSectionsApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { categoryKey, isBarSection, routeFor, routeWords, sectionRoutes, stationKey } from '@verlo/shared/lib/kitchenRoutes';
import RoutePicker from '../../components/menu/RoutePicker';
import { kitchenView, saveBody } from '../../lib/kitchenStations';

// Kitchen stations (Carl, Oct 2026: "our dishes go through 4 stations: chicken, fry, build
// and pass"). The stations in the order food moves through them, then each menu section's
// route through them. The pass is always there and always last: no route can leave it out
// (Martin). A dish can have its own route, in its editor. A group can keep one set of
// stations for every site or let each site run its own; a single site never sees the choice.
// A venue's manager sets them up (#303); someone without `kitchen.configure` only looks.

const snapshot = (stations, routes) => JSON.stringify({ stations, routes });

const KitchenStationsPage = () => {
  const { site, sites, loaded } = useSite();
  const { hasPermission } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [stations, setStations] = useState([]);   // [{ key, name }], in kitchen order
  const [routes, setRoutes] = useState({});       // { section key: [station keys] }
  const [saved, setSaved] = useState('');
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);   // the section key being re-routed
  const [newName, setNewName] = useState('');
  const [switching, setSwitching] = useState(null); // 'own' | 'same', while confirming
  const [saving, setSaving] = useState(false);
  // The site's kitchen screens and what each shows, set on the screens themselves (M2).
  const [screens, setScreens] = useState(null);

  // `here` is the site picked in the header, or the only one this person sees. With each
  // site its own and none picked there is nothing honest to show (`needsSite`).
  const { perSite, here, needsSite, canEdit, mode, everywhere } = kitchenView({
    cfg, site, sites, canConfigure: hasPermission('kitchen.configure'),
  });
  const hereSite = (sites || []).find((s) => s.id === here) || null;

  const load = async () => {
    try {
      const [{ data }, s, i] = await Promise.all([
        financialConfigApi.getSettings(here || undefined),
        menuSectionsApi.list(here || undefined),
        menuApi.getAll(undefined, here || undefined),
      ]);
      const st = (data.kitchen_stations || []).map(({ key, name }) => ({ key, name }));
      const r = sectionRoutes(data.kitchen_stations || [], data.kitchen_routes || {});
      setCfg(data);
      setStations(st);
      setRoutes(r);
      setSaved(snapshot(st, r));
      setSections(s.data || []);
      setItems(i.data || []);
      setEditing(null);
    } catch {
      toast.error("Couldn't load the kitchen stations");
    }
  };
  // Once the site list is in: before it, a one-site manager's `here` is still null and the
  // page would load the business's list.
  useEffect(() => { if (loaded) load(); }, [loaded, here]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!here) { setScreens(null); return; }
    kitchenScreensApi.list(here).then((r) => setScreens(r.data || [])).catch(() => setScreens([]));
  }, [here]);
  const showsWords = (key) => (key === 'kitchen' ? 'The Pass' : stations.find((st) => st.key === key)?.name || key);

  const dirty = cfg && snapshot(stations, routes) !== saved;

  // The menu's food sections, once each, in the till's order; drinks go to the bar.
  const { food, bar } = useMemo(() => {
    const seen = new Map();
    [...sections.map((s) => s.name), ...items.map((it) => it.category)].forEach((name) => {
      if (name && !seen.has(categoryKey(name))) seen.set(categoryKey(name), name);
    });
    const all = [...seen.entries()].map(([key, name]) => ({ key, name }));
    return { food: all.filter((s) => !isBarSection(s.name)), bar: all.filter((s) => isBarSection(s.name)) };
  }, [sections, items]);

  const dishesIn = (key) => items.filter((it) => categoryKey(it.category) === key);
  const ownRoute = (it) => {
    const mine = (it.kitchen_route || []).filter((k) => stations.some((s) => s.key === k));
    return mine.length > 0 || stations.some((s) => s.key === it.kitchen_station);
  };
  const through = useMemo(() => {
    const n = {};
    items.filter((it) => !isBarSection(it.category))
      .forEach((it) => routeFor(it, stations, routes).forEach((k) => { n[k] = (n[k] || 0) + 1; }));
    return n;
  }, [items, stations, routes]);

  const move = (i, by) => {
    const j = i + by;
    if (j < 0 || j >= stations.length) return;
    const next = [...stations];
    [next[i], next[j]] = [next[j], next[i]];
    setStations(next);
  };
  // The same drag as Structure (one shared piece): a gap where it lands, the page scrolls at
  // its edges, and the arrow keys on the handle move a station one place.
  const drag = useDragList(stations, (s) => s.key, (keys) => setStations(keys.map((k) => stations.find((s) => s.key === k))));
  const rename = (i, name) => setStations(stations.map((s, j) => (j === i ? { ...s, name } : s)));
  // A station taken out comes out of every route with it, so its dishes go on to the next
  // station on their route (or the pass).
  const remove = (key) => {
    setStations(stations.filter((s) => s.key !== key));
    setRoutes(Object.fromEntries(Object.entries(routes).map(([k, r]) => [k, r.filter((x) => x !== key)])));
  };
  const add = () => {
    const name = newName.trim();
    if (!name) return;
    setStations([...stations, { key: stationKey(name, stations.map((s) => s.key)), name }]);
    setNewName('');
  };

  const cleaned = () => {
    const keys = new Set(stations.map((s) => s.key));
    const st = stations.map((s) => ({ key: s.key, name: s.name.trim() }));
    if (st.some((s) => !s.name)) throw new Error('Every station needs a name');
    const r = Object.fromEntries(Object.entries(routes).map(([k, route]) => [k, route.filter((x) => keys.has(x))]));
    return { st, r };
  };

  const save = async () => {
    let body;
    try {
      const { st, r } = cleaned();
      body = saveBody({ perSite, here, stations: st, routes: r });
    } catch (e) {
      toast.error(e.message);
      return;
    }
    setSaving(true);
    try {
      await financialConfigApi.updateSettings(body);
      toast.success('Kitchen stations saved. The tills pick them up at the next sync.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the kitchen stations");
    } finally {
      setSaving(false);
    }
  };

  // Same at every site ⇄ each site its own. Going to "its own" gives every site a copy of
  // what is shown, so nothing changes until someone edits a site; going back makes what is
  // shown the business's and drops every site's own list.
  const switchMode = async (to) => {
    let st; let r;
    try { ({ st, r } = cleaned()); } catch (e) { toast.error(e.message); return; }
    const venues = Object.fromEntries((sites || []).map((s) => [s.id, to === 'own'
      ? { kitchen_stations: st, kitchen_routes: r }
      : { kitchen_stations: null, kitchen_routes: null }]));
    setSaving(true);
    try {
      await financialConfigApi.updateSettings(to === 'own'
        ? { venues }
        : { default_kitchen_stations: st, default_kitchen_routes: r, venues });
      toast.success(to === 'own' ? 'Each site now has its own stations, starting from these' : 'Every site now uses these stations');
      setSwitching(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't change that");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="space-y-6"><PageHeader title="Kitchen stations" /><Loading /></div>;

  return (
    <div className="space-y-6" data-testid="kitchen-stations-page">
      <PageHeader
        title="Kitchen stations"
        purpose="The stations food goes through in your kitchen, and which dishes go where. Every dish ends at the Pass."
        actions={canEdit && (
          <>
            {dirty && <Button variant="outline" onClick={load} disabled={saving} data-testid="kitchen-undo">Undo changes</Button>}
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={!dirty || saving || needsSite} data-testid="kitchen-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
      />

      {!canEdit && (
        <Notice tone="info" word="Kitchen" data-testid="kitchen-read-only">
          A manager or anyone above them can change the stations. Your account can see them.
        </Notice>
      )}

      {needsSite ? (
        <Notice tone="info" word="Sites" title="Each site sets up its own stations">
          Choose a site in the header to see and change its stations.
        </Notice>
      ) : (
        <>
          <section className="rounded-lg border border-hairline bg-white p-5" data-testid="kitchen-stations">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-[16px] font-semibold text-ink">{perSite && hereSite ? `${hereSite.name}'s stations` : 'Your stations'}</h2>
                <p className="mt-1 max-w-2xl text-[13.5px] text-ink-soft">
                  In the order food moves through the kitchen. It's the order of the station buttons on the kitchen screens.
                </p>
                {/* The shared list reaches sites this person can't see: say so before they save. */}
                {everywhere && (
                  <p className="mt-1 max-w-2xl text-[13.5px] text-ink" data-testid="kitchen-shared-everywhere">
                    Every site in the group uses these stations, so a change here changes them everywhere.
                  </p>
                )}
              </div>
              {/* The switch rewrites every site's own list, so it is only for someone who covers
                  every site; anyone else over more than one, or on a list shared with sites
                  they can't see, sees which it is (#303). */}
              {mode === 'switch' && (
                <Segmented
                  value={switching || (perSite ? 'own' : 'same')}
                  onChange={(v) => setSwitching(v === (perSite ? 'own' : 'same') ? null : v)}
                  options={[{ value: 'same', label: 'Same at every site', testId: 'kitchen-mode-same' },
                    { value: 'own', label: 'Each site its own', testId: 'kitchen-mode-own' }]}
                />
              )}
              {mode === 'words' && (
                <span className="text-[13.5px] text-ink-soft" data-testid="kitchen-mode-words">
                  {perSite ? 'Each site its own' : 'Same at every site'}
                </span>
              )}
            </div>

            {switching && (
              <Notice tone="warn" word="Sites" className="mt-4"
                title={switching === 'own' ? 'Give each site its own stations?' : 'Use these stations at every site?'}
                action={(
                  <span className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSwitching(null)} disabled={saving}>Not now</Button>
                    <Button size="sm" className="bg-verlo hover:bg-verlo-hover" onClick={() => switchMode(switching)} disabled={saving} data-testid="kitchen-mode-confirm">
                      {switching === 'own' ? 'Give each its own' : 'Use these everywhere'}
                    </Button>
                  </span>
                )}>
                {switching === 'own'
                  ? 'Every site starts with a copy of the stations and routes below, and can change its own from then on.'
                  : "Every site will use the stations and routes below. The other sites' own lists are dropped."}
              </Notice>
            )}

            <div className="mt-4 overflow-hidden rounded-lg border border-hairline" {...drag.listProps}>
              {drag.rows.map((st, i) => (
                <div
                  key={st.key}
                  className={`grid grid-cols-[28px_22px_minmax(0,300px)_1fr_auto] items-center gap-3 border-b border-hairline px-3 py-2.5 transition-colors ${drag.isHeld(st) ? 'bg-verlo-tint outline-dashed outline-1 -outline-offset-1 outline-verlo/50 [&>*]:opacity-60' : ''}`}
                  {...drag.rowProps(st, i)}
                  data-testid={`station-${st.key}`}
                >
                  {canEdit ? (
                    <button type="button" className="cursor-grab rounded p-1 text-ink-faint hover:bg-hover-tint hover:text-ink active:cursor-grabbing"
                      {...drag.handleProps(st)}
                      onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); move(i, -1); } if (e.key === 'ArrowDown') { e.preventDefault(); move(i, 1); } }}
                      aria-label={`Move ${st.name}: drag it, or use the arrow keys`} data-testid={`station-handle-${st.key}`}><GripVertical size={16} /></button>
                  ) : <span />}
                  <span className="font-heading text-[13px] font-bold tabular-nums text-ink-faint">{i + 1}</span>
                  {canEdit
                    ? <Input value={st.name} onChange={(e) => rename(i, e.target.value)} aria-label={`Station ${i + 1}`} className="h-9" data-testid={`station-name-${st.key}`} />
                    : <span className="font-semibold text-ink" data-testid={`station-name-${st.key}`}>{st.name}</span>}
                  <span className="text-[13px] tabular-nums text-ink-soft">
                    {through[st.key] ? `${through[st.key]} dish${through[st.key] === 1 ? '' : 'es'} go${through[st.key] === 1 ? 'es' : ''} through it` : 'No dishes go through it yet'}
                  </span>
                  {canEdit
                    ? <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-ink-faint" onClick={() => remove(st.key)} aria-label={`Remove ${st.name}`} data-testid={`station-remove-${st.key}`}><Trash2 size={15} /></Button>
                    : <span />}
                </div>
              ))}
              <div className="grid grid-cols-[28px_22px_minmax(0,300px)_1fr] items-center gap-3 bg-surface-2 px-3 py-3" data-testid="station-pass">
                <span />
                <span className="font-heading text-[13px] font-bold tabular-nums text-ink-faint">{stations.length + 1}</span>
                <span className="font-semibold text-ink">Pass</span>
                <span className="text-[13px] text-ink-soft">Every dish ends here. Always last.</span>
              </div>
            </div>

            {canEdit && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                       placeholder="A new station, e.g. Grill" className="h-9 w-64" data-testid="station-new-name" />
                <Button variant="outline" size="sm" className="h-9" onClick={add} disabled={!newName.trim()} data-testid="station-add"><Plus size={14} className="mr-1.5" />Add a station</Button>
                <span className="text-[12.5px] text-ink-faint">Removing a station sends its dishes on to the next one in their route.</span>
              </div>
            )}
          </section>

          <section className="rounded-lg border border-hairline bg-white p-5" data-testid="kitchen-routes">
            <h2 className="text-[16px] font-semibold text-ink">What goes through each station</h2>
            <p className="mt-1 max-w-2xl text-[13.5px] text-ink-soft">
              Each menu section has a route, and its dishes follow it. A dish that goes another way gets its own route, in the dish's editor.
            </p>
            {stations.length === 0 ? (
              <Notice tone="info" word="Pass" className="mt-4">No stations yet, so every dish goes straight to the Pass. Add a station above to give sections a route.</Notice>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[640px] text-[14px]">
                  <thead>
                    <tr className="border-b border-hairline text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                      <th className="py-2 pr-3 font-bold">Section</th>
                      <th className="py-2 pr-3 font-bold">Dishes</th>
                      <th className="py-2 pr-3 font-bold">Goes through</th>
                      <th className="py-2 pr-3 font-bold">Their own route</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {food.map((sec) => {
                      const dishes = dishesIn(sec.key);
                      const theirOwn = dishes.filter(ownRoute).length;
                      const route = (routes[sec.key] || []).filter((k) => stations.some((s) => s.key === k));
                      const isEditing = editing === sec.key;
                      return (
                        <tr key={sec.key} className={`border-b border-hairline align-middle ${isEditing ? 'bg-surface-2' : ''}`} data-testid={`route-${sec.key}`}>
                          <td className="py-3 pr-3 font-semibold text-ink">{sec.name}</td>
                          <td className="py-3 pr-3 tabular-nums text-ink-soft">{dishes.length}</td>
                          {isEditing ? (
                            <td className="py-3 pr-3" colSpan={2}>
                              <p className="mb-2 text-[12.5px] text-ink-soft">Tap the stations in the order the food goes through them</p>
                              <RoutePicker stations={stations} value={route} onChange={(r) => setRoutes({ ...routes, [sec.key]: r })} testId={`route-pick-${sec.key}`} />
                              {theirOwn > 0 && <p className="mt-2 text-[12.5px] text-ink-faint">{theirOwn} of its {dishes.length} dishes have their own route and won't change.</p>}
                            </td>
                          ) : (
                            <>
                              <td className="py-3 pr-3 text-ink" data-testid={`route-words-${sec.key}`}>
                                {route.length ? <>{routeWords(route, stations)}<span className="text-ink-faint">, then the Pass</span></> : <span className="text-ink-soft">Straight to the Pass</span>}
                              </td>
                              <td className="py-3 pr-3 text-ink-soft">{theirOwn ? `${theirOwn} dish${theirOwn === 1 ? '' : 'es'}` : 'None'}</td>
                            </>
                          )}
                          <td className="py-3 text-right">
                            {canEdit && (isEditing
                              ? <Button size="sm" className="bg-verlo hover:bg-verlo-hover" onClick={() => setEditing(null)} data-testid={`route-done-${sec.key}`}>Done</Button>
                              : <Button size="sm" variant="ghost" className="text-verlo" onClick={() => setEditing(sec.key)} data-testid={`route-change-${sec.key}`}>Change</Button>)}
                          </td>
                        </tr>
                      );
                    })}
                    {bar.length > 0 && (
                      <tr className="text-ink-faint">
                        <td className="py-3 pr-3 font-semibold">{bar.map((b) => b.name).join(', ')}</td>
                        <td className="py-3 pr-3 tabular-nums">{bar.reduce((n, b) => n + dishesIn(b.key).length, 0)}</td>
                        <td className="py-3 pr-3" colSpan={3}>The bar</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rounded-lg border border-hairline bg-white p-5" data-testid="kitchen-screens">
            <h2 className="text-[16px] font-semibold text-ink">Screens</h2>
            <p className="mt-1 max-w-2xl text-[13.5px] text-ink-soft">
              What each kitchen screen shows. A manager sets it on the screen itself, under This screen › Change.
            </p>
            {!here ? (
              <p className="mt-3 text-[13.5px] text-ink-faint">Choose a site in the header to see its screens.</p>
            ) : screens && screens.length ? (
              <table className="mt-3 w-full text-[13.5px]">
                <thead>
                  <tr className="border-b border-hairline text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                    <th className="py-2 pr-3">Screen</th><th className="py-2 pr-3">Shows</th><th className="py-2">Set by</th>
                  </tr>
                </thead>
                <tbody>
                  {screens.map((sc) => (
                    <tr key={sc.id} className="border-b border-hairline" data-testid={`kitchen-screen-${sc.id}`}>
                      <td className="py-2.5 pr-3 font-semibold text-ink">{sc.name || 'A kitchen screen'}</td>
                      <td className="py-2.5 pr-3 text-ink">{showsWords(sc.station)}</td>
                      <td className="py-2.5 text-ink-soft">
                        {[sc.set_by_name, sc.set_at && new Date(sc.set_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })].filter(Boolean).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="mt-3 text-[13.5px] text-ink-faint">
                {screens ? 'No screen has been set yet. Until one is, a kitchen screen shows the Pass.' : 'Loading…'}
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default KitchenStationsPage;
